import { observer } from 'mobx-react-lite'
import { lazy, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeftOutlined } from '@ant-design/icons'

import { useStore } from 'store/useStore'
import { ContainerLK } from 'sections/LKSection/shared/components/ContainerLK/ContainerLK'
import { Margin, Padding } from 'components/ui'
import { Button, Flex, Skeleton, Title } from 'components/uiKit'
import { useAsyncEffect } from 'utils/hooks/useAsyncEffect'
import { LKRoutesEnum } from 'shared/constants/routes'
import { SEARCH_PARAMS } from 'shared/constants/values'

import { PvzKeys } from '../../data'
import { PvzItemForm } from './PvzItemForm/PvzItemForm'

const getKey = (key: string) => `${PvzKeys.pvzItemPage}.${key}`

const PvzItemPage = observer(() => {
  const { viewPvzUiStore: uiStore } = useStore()

  const { pvzId } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const location = useLocation()

  const pvz = uiStore.selectedPvz

  const searchParams = new URLSearchParams(location.search)
  const pageFromUrl = Number(searchParams.get(SEARCH_PARAMS.page))
  const pageSizeFromUrl = Number(searchParams.get(SEARCH_PARAMS.pageSize))

  useEffect(() => {
    if (pageFromUrl && pageSizeFromUrl) {
      uiStore.setPage(+pageFromUrl)
      uiStore.setPageSize(+pageSizeFromUrl)
    }
  }, [pageFromUrl, pageSizeFromUrl, uiStore])

  useAsyncEffect(async () => {
    if (pvzId) {
      void uiStore.switchPvz(+pvzId)

      if (uiStore.pvzList.length === 0) {
        await uiStore.getPvzList()
      }

      uiStore.setSelectedPvz(pvz ?? uiStore.getPvz(+pvzId))
    }
  }, [pvzId])

  const backButtonHandler = () => {
    navigate(LKRoutesEnum.pvz)
  }

  return (
    <ContainerLK>
      <Padding py={32} style={{ width: '100%' }}>
        <Flex vertical style={{ maxWidth: 600 }}>
          <Margin mb={32}>
            <Flex gap={16} align="center">
              <Button shape="circle" icon={<ArrowLeftOutlined />} onClick={backButtonHandler} />

              <Title>{t(getKey('ПВЗ №'), { count: pvz?.externalId ? pvz.externalId : 0 })}</Title>
            </Flex>
          </Margin>

          <Skeleton loading={uiStore.loadingPvzList}>
            {pvz ? (
              <PvzItemForm pvz={pvz} />
            ) : (
              <Flex>
                <Title type="secondary" level={4}>
                  {t(getKey('Информации по данному ПВЗ нет'))}
                </Title>
              </Flex>
            )}
          </Skeleton>
        </Flex>
      </Padding>
    </ContainerLK>
  )
})

export default PvzItemPage

export const PvzItemPageAsync = lazy(() => import('./PvzItemPage'))
